import * as LabelPrimitive from '@radix-ui/react-label';
import { cva } from 'class-variance-authority';
import { Check, Minus } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '../lib/cn';
import { focusRing } from '../lib/focus-ring';
import { useFieldContext, useFieldProps } from '../lib/field-context';

/**
 * Three states, not two. `'indeterminate'` is the "some of these are selected"
 * state a select-all control needs — a user can never produce it by clicking,
 * only an owner can set it, which is why `onCheckedChange` reports a plain
 * boolean.
 */
export type CheckedState = boolean | 'indeterminate';

/**
 * Exported so an app-authored checkbox — one rendered by a table library, say —
 * can wear the same box without re-implementing it.
 *
 * The `<input>` *is* the box: `appearance-none` strips the native rendering and
 * the tick is drawn over it. That keeps the focus ring, the hover state and the
 * disabled state on the real focusable element instead of a decorative sibling.
 */
export const checkboxVariants = cva(
  cn(
    'peer m-0 shrink-0 cursor-pointer appearance-none rounded-xs border border-line-strong bg-ink/55',
    'transition-[background-color,border-color,box-shadow] duration-150 ease-wend',
    'hover:enabled:border-white/25',
    'data-[state=checked]:border-transparent data-[state=checked]:bg-brand-gradient',
    'data-[state=indeterminate]:border-transparent data-[state=indeterminate]:bg-brand-gradient',
    'disabled:cursor-not-allowed disabled:opacity-55',
    'aria-[invalid=true]:border-bad aria-[invalid=true]:focus-visible:outline-bad',
    // Windows high-contrast mode throws away background-image, which would
    // leave an empty square. Hand the checkbox back to the platform there: the
    // native rendering draws the tick *and* the indeterminate dash, because the
    // DOM `indeterminate` property is set either way.
    'forced-colors:appearance-auto',
    focusRing,
  ),
  {
    variants: {
      size: {
        sm: 'size-4',
        md: 'size-[1.125rem]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

/** Sizes that belong to the glyph and the label rather than to the box. */
const sizeDetail = {
  sm: { glyph: 'size-3', label: 'text-[0.85rem]', offset: 'mt-px' },
  md: { glyph: 'size-3.5', label: 'text-[0.95rem]', offset: 'mt-0.5' },
} as const;

export type CheckboxSize = keyof typeof sizeDetail;

export interface CheckboxProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'checked' | 'defaultChecked' | 'size' | 'type'
  > {
  /** Controlled state. Leave undefined to let the Checkbox hold its own. */
  checked?: CheckedState;
  /** Initial state when uncontrolled. */
  defaultChecked?: CheckedState;
  /**
   * Fires with the state the user just produced. Always a boolean: clicking an
   * indeterminate checkbox checks it, exactly like the platform's own.
   */
  onCheckedChange?: (checked: boolean) => void;
  /** Rendered beside the box and wired to it with `htmlFor`. */
  label?: ReactNode;
  /** Second line under the label, referenced by `aria-describedby`. */
  description?: ReactNode;
  size?: CheckboxSize;
  /** Force the error styling without a surrounding `<FormField>`. */
  invalid?: boolean;
  /** Classes for the box. `className` styles the whole control row. */
  boxClassName?: string;
}

/**
 * A checkbox with a real `<input type="checkbox">` underneath, so the keyboard
 * model, the mixed state and form participation are the platform's own: Tab
 * reaches it, Space toggles it, and `indeterminate` is exposed to assistive
 * technology as `aria-checked="mixed"` without any ARIA of our own.
 *
 *     <Checkbox
 *       checked={allSelected ? true : someSelected ? 'indeterminate' : false}
 *       onCheckedChange={toggleAll}
 *       label="All datasets"
 *     />
 *
 * Naming, in one rule: a Checkbox that renders its own `label` owns a generated
 * id; one without a label adopts the surrounding `<FormField>`'s id so that
 * field's `<Label>` names it. See the package README for why.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    checked: checkedProp,
    defaultChecked,
    onCheckedChange,
    onChange,
    label,
    description,
    size = 'md',
    invalid,
    disabled,
    id,
    className,
    boxClassName,
    'aria-describedby': describedBy,
    'aria-invalid': ariaInvalid,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const field = useFieldContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [internalChecked, setInternalChecked] = useState<CheckedState>(defaultChecked ?? false);

  const checked = checkedProp === undefined ? internalChecked : checkedProp;
  const state =
    checked === 'indeterminate' ? 'indeterminate' : checked === true ? 'checked' : 'unchecked';

  const hasLabel = label !== undefined && label !== null;
  const hasDescription = description !== undefined && description !== null;

  // A Checkbox that labels itself must NOT take the field id: a group of them
  // inside one <FormField> would then all share a single id, which is invalid
  // markup and leaves every label pointing at the first box. Without a label of
  // its own it does take the id, so the field's <Label htmlFor> names it.
  const ownId = `wend-checkbox-${generatedId}`;
  const inputId = id ?? (hasLabel ? ownId : (field?.id ?? ownId));
  const descriptionId = hasDescription ? `${inputId}-description` : undefined;

  // An explicit aria-invalid always wins over the field's; `undefined` lets the
  // surrounding FormField decide. Same rule as Input.
  const resolvedInvalid: boolean | undefined =
    ariaInvalid === undefined
      ? invalid === true
        ? true
        : undefined
      : ariaInvalid !== false && ariaInvalid !== 'false';

  const ownDescribedBy = [describedBy, descriptionId]
    .filter((value): value is string => Boolean(value))
    .join(' ');

  // useFieldProps appends the field's description/error ids, so a checkbox in a
  // group keeps its own description *and* inherits the field's.
  const fieldProps = useFieldProps({
    id: inputId,
    'aria-describedby': ownDescribedBy === '' ? undefined : ownDescribedBy,
    'aria-invalid': resolvedInvalid,
  });

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref !== null && ref !== undefined) {
        ref.current = node;
      }
    },
    [ref],
  );

  // `indeterminate` is a DOM property, not an attribute, so React cannot render
  // it. Deliberately no dependency array: clicking a checkbox clears the flag in
  // the DOM and React does not put it back, so an owner that keeps the state
  // mixed would silently lose it. Re-asserting a boolean each render is free.
  useEffect(() => {
    const node = inputRef.current;
    if (node !== null) node.indeterminate = state === 'indeterminate';
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.currentTarget.checked;
    if (checkedProp === undefined) setInternalChecked(next);
    onCheckedChange?.(next);
    onChange?.(event);
  };

  const detail = sizeDetail[size];
  const glyphClass = cn(
    'pointer-events-none absolute inset-0 m-auto text-white opacity-0',
    'transition-opacity duration-100 ease-wend forced-colors:hidden',
    detail.glyph,
  );

  return (
    <span className={cn('inline-flex items-start gap-2.5', className)}>
      <span className={cn('relative inline-flex shrink-0', hasLabel && detail.offset)}>
        <input
          {...rest}
          ref={setInputRef}
          type="checkbox"
          checked={checked === true}
          disabled={disabled}
          data-state={state}
          onChange={handleChange}
          className={cn(checkboxVariants({ size }), boxClassName)}
          {...fieldProps}
        />

        <Check aria-hidden="true" strokeWidth={3} className={cn(glyphClass, 'peer-data-[state=checked]:opacity-100')} />
        <Minus aria-hidden="true" strokeWidth={3.5} className={cn(glyphClass, 'peer-data-[state=indeterminate]:opacity-100')} />
      </span>

      {(hasLabel || hasDescription) && (
        <span className="flex min-w-0 flex-col gap-0.5">
          {hasLabel && (
            // Radix's Label, not a bare <label>: a select-all gets clicked
            // repeatedly, and a native label selects its own text on the second
            // click. The text itself stays sans — mono uppercase is for field
            // labels, which is what the `Label` component is for.
            <LabelPrimitive.Root
              htmlFor={inputId}
              className={cn(
                'leading-snug text-text',
                detail.label,
                disabled === true ? 'cursor-not-allowed opacity-55' : 'cursor-pointer',
              )}
            >
              {label}
            </LabelPrimitive.Root>
          )}

          {hasDescription && (
            <span
              id={descriptionId}
              className={cn(
                'text-[0.8rem] leading-snug text-muted',
                disabled === true && 'opacity-55',
              )}
            >
              {description}
            </span>
          )}
        </span>
      )}
    </span>
  );
});
